/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * ToastProvider — Real-time toast notification system.
 *
 * Migrated from Socket.io to Laravel Echo + Reverb.
 * Previously listened on socket.on('notification:new') / socket.on('toast:show').
 * Now listens on the private user channel via Echo.
 *
 * Also exposes useToast() for programmatic toasts anywhere in the app.
 *
 * Usage:
 *   // Wrap app: <ToastProvider>
 *   // In any component:
 *   const { addToast } = useToast();
 *   addToast({ type: 'success', title: 'Saved!', message: 'Budget updated.' });
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useEcho } from '@context/EchoContext';
import { useAuth } from '@hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const ToastContext = createContext({ addToast: () => {} });
export const useToast = () => useContext(ToastContext);

// ─── Configuration ───────────────────────────────────────────────────────────
const MAX_TOASTS        = 4;
const DEFAULT_DURATION  = 6000;  // ms
const ANIMATION_DURATION = 350;  // ms

// ─── Type styling ─────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  info:    { bg: '#EFF6FF', border: '#3B82F6', color: '#1E40AF', defaultIcon: 'ℹ️' },
  success: { bg: '#ECFDF5', border: '#10B981', color: '#065F46', defaultIcon: '✅' },
  warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#92400E', defaultIcon: '⚠️' },
  error:   { bg: '#FEF2F2', border: '#EF4444', color: '#991B1B', defaultIcon: '❌' },
  rsvp:    { bg: '#FDF2F8', border: '#EC4899', color: '#9D174D', defaultIcon: '🎉' },
  booking: { bg: '#F5F3FF', border: '#8B5CF6', color: '#5B21B6', defaultIcon: '📅' },
  review:  { bg: '#FFF7ED', border: '#F97316', color: '#9A3412', defaultIcon: '⭐' },
  photo:   { bg: '#F0F9FF', border: '#0EA5E9', color: '#075985', defaultIcon: '📸' },
  gift:    { bg: '#FDF4FF', border: '#D946EF', color: '#86198F', defaultIcon: '🎁' },
  budget:  { bg: '#ECFDF5', border: '#059669', color: '#064E3B', defaultIcon: '💰' },
  system:  { bg: '#F9FAFB', border: '#6B7280', color: '#1F2937', defaultIcon: '🔔' },
};

// ─── ToastContainer ───────────────────────────────────────────────────────────
const ToastContainer = ({ toasts, onDismiss, onNavigate }) => {
  if (toasts.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 99999,
      display: 'flex', flexDirection: 'column', gap: 10,
      maxWidth: 380, width: '100%', pointerEvents: 'none',
    }}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} onNavigate={onNavigate} />
      ))}
    </div>
  );
};

// ─── Single Toast ─────────────────────────────────────────────────────────────
const ToastItem = ({ toast, onDismiss, onNavigate }) => {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);
  const config   = TYPE_CONFIG[toast.type] || TYPE_CONFIG.info;

  const startDismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), ANIMATION_DURATION);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    if (toast.duration !== 0) {
      timerRef.current = setTimeout(startDismiss, toast.duration || DEFAULT_DURATION);
    }
    return () => clearTimeout(timerRef.current);
  }, [startDismiss, toast.duration]);

  const handleClick = () => {
    if (toast.link) { onNavigate(toast.link); startDismiss(); }
  };

  return (
    <div
      style={{
        background: config.bg,
        borderLeft: `4px solid ${config.border}`,
        borderRadius: '0 10px 10px 0',
        padding: '12px 14px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
        pointerEvents: 'auto',
        cursor: toast.link ? 'pointer' : 'default',
        display: 'flex', gap: 10, alignItems: 'flex-start',
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateX(100%)' : 'translateX(0)',
        transition: `opacity ${ANIMATION_DURATION}ms ease, transform ${ANIMATION_DURATION}ms ease`,
        animation: 'toastSlideIn 0.35s ease-out',
      }}
      onClick={handleClick}
      onMouseEnter={() => clearTimeout(timerRef.current)}
      onMouseLeave={() => {
        if (toast.duration !== 0) {
          timerRef.current = setTimeout(startDismiss, 2000);
        }
      }}
    >
      <span style={{ fontSize: '1.2rem', lineHeight: 1, flexShrink: 0, marginTop: 1 }}>
        {toast.icon || config.defaultIcon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: config.color, lineHeight: 1.3, marginBottom: toast.message ? 2 : 0 }}>
          {toast.title}
        </div>
        {toast.message && (
          <div style={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {toast.message}
          </div>
        )}
        {toast.actionLabel && toast.link && (
          <div style={{ marginTop: 4 }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: config.border, textDecoration: 'underline', textUnderlineOffset: 2 }}>
              {toast.actionLabel} →
            </span>
          </div>
        )}
      </div>
      <button
        onClick={e => { e.stopPropagation(); startDismiss(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#9CA3AF', padding: 0, lineHeight: 1, flexShrink: 0 }}
        aria-label="Dismiss"
      >✕</button>
    </div>
  );
};

// ─── Global keyframe styles ───────────────────────────────────────────────────
const STYLE_ID = '__toast_styles__';
const ensureStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes toastSlideIn {
      from { opacity: 0; transform: translateX(100%); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(style);
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const { echo, connected } = useEcho();
  const { user }            = useAuth();
  const navigateRef         = useRef(null);

  try {
    const navigate = useNavigate();
    navigateRef.current = navigate;
  } catch {
    navigateRef.current = (path) => { window.location.href = path; };
  }

  ensureStyles();

  const addToast = useCallback((toast) => {
    const id = toast.id || `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setToasts(prev => [{ ...toast, id, createdAt: new Date() }, ...prev].slice(0, MAX_TOASTS));
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleNavigate = useCallback((path) => {
    navigateRef.current?.(path);
  }, []);

  // ─── Subscribe to the user's private Echo channel ──────────────────────────
  // Replaces: socket.on('notification:new', ...) and socket.on('toast:show', ...)
  // Channel:  App.Models.User.{userId}  (private, authorized via channels.php)
  useEffect(() => {
    if (!echo || !connected || !user?.id) return;

    const channel = echo.private(`App.Models.User.${user.id}`);

    // Persistent notification (DB-backed) — also show as popup toast
    // Server event: App\Events\Notifications\NotificationNew
    channel.listen('.NotificationNew', (data) => {
      addToast({
        id:          data.id,
        type:        data.type || 'info',
        title:       data.title,
        message:     data.message,
        icon:        data.icon,
        link:        data.link,
        actionLabel: data.actionLabel,
      });
    });

    // Ephemeral toast (no DB entry) — show immediately and forget
    // Server event: App\Events\Notifications\ToastShow
    channel.listen('.ToastShow', (data) => {
      addToast({
        id:       data.id,
        type:     data.type || 'info',
        title:    data.title,
        message:  data.message,
        icon:     data.icon,
        link:     data.link,
        duration: data.duration,
      });
    });

    return () => {
      // Leave only when user changes — EchoProvider also tears down on logout.
      // echo.leave here would conflict with useRealtimeNotifications on the
      // same channel, so we just stop listening and let EchoProvider manage
      // the connection lifecycle.
      channel.stopListening('.NotificationNew');
      channel.stopListening('.ToastShow');
    };
  }, [echo, connected, user?.id, addToast]);

  return (
    <ToastContext.Provider value={{ addToast, dismissToast, toasts }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} onNavigate={handleNavigate} />
    </ToastContext.Provider>
  );
};

export default ToastProvider;
